import type {
  AgreementVersionMap,
  AgreementsPatchRequest,
  RequirementConsent,
  RequirementStatus,
  RequirementTerm,
} from "@/types/agreement";

export const HOTELS_REQUIREMENTS = ["opera_membership", "thon_plus_membership"] as const;

export const getUnsatisfiedRequirements = (
  requirementStatus: RequirementStatus | null,
): { terms: RequirementTerm[]; consents: RequirementConsent[] } | null => {
  if (!requirementStatus || requirementStatus === "Satisfied") {
    return null;
  }

  return requirementStatus.Unsatisfied;
};

export const getGrantedVersionMap = <
  T extends {
    slug: string;
    granted: boolean;
    granted_version: number | null;
    latest_version: number;
  },
>(
  items: T[],
): AgreementVersionMap => {
  return items.reduce<AgreementVersionMap>((acc, item) => {
    if (item.granted) {
      acc[item.slug] = item.granted_version ?? item.latest_version;
    }

    return acc;
  }, {});
};

export const toggleVersionSelection = (
  previous: AgreementVersionMap,
  slug: string,
  version: number,
  isChecked: boolean,
): AgreementVersionMap => {
  if (isChecked) {
    return { ...previous, [slug]: version };
  }

  const next = { ...previous };
  delete next[slug];
  return next;
};

export const hasUncheckedRequiredTerms = (
  terms: RequirementTerm[],
  termVersions: AgreementVersionMap,
): boolean => {
  return terms.some((term) => !termVersions[term.slug]);
};

export const buildAgreementsPatch = (
  terms: { slug: string }[],
  consents: { slug: string }[],
  termSelections: AgreementVersionMap,
  consentSelections: AgreementVersionMap,
): AgreementsPatchRequest => {
  return {
    terms: terms.map((term) => ({
      slug: term.slug,
      granted_version: termSelections[term.slug] ?? null,
    })),
    consents: consents.map((consent) => ({
      slug: consent.slug,
      granted_version: consentSelections[consent.slug] ?? null,
    })),
  };
};

export const getTermLabelMarkdown = (term: {
  short_text?: string;
  long_text?: string;
  text?: string;
}): string => term.short_text || term.long_text || term.text || "";

export const getConsentLabelMarkdown = (consent: { text?: string; short_text?: string }): string =>
  consent.text || consent.short_text || "";
